
import {
  $applyNodeReplacement,
  createEditor,
  DecoratorNode,
  LineBreakNode,
  ParagraphNode,
  RootNode,
  TextNode,
} from 'lexical';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { HashtagNode } from '@lexical/hashtag';
import { EmojiNode } from './EmojiNode';
import { KeywordNode } from './KeywordNode';
import { MentionNode } from './MentionNode';
import ImageComponent from './ImageComponent';


export class ImageNode extends DecoratorNode {
  __src;
  __altText;
  __width;
  __height;
  __maxWidth;
  __showCaption;
  __caption;
  __captionsEnabled;

  static importDOM() {
    return {
      img: (node) => ({
        conversion: (element) => {
          const src = element.getAttribute('src');
          const altText = element.getAttribute('alt') || '';
          const width = element.getAttribute('width') || 'inherit';
          const height = element.getAttribute('height') || 'inherit';
          const maxWidth = element.getAttribute('data-max-width') || 500;
          const showCaption = element.getAttribute('data-show-caption') === 'true';

          // Import caption data if available
          let caption = createEditor({
            namespace: 'ImageNodeCaption',
            nodes: [
              RootNode, TextNode, LineBreakNode, ParagraphNode,
              LinkNode, AutoLinkNode, HashtagNode,
              EmojiNode, KeywordNode, MentionNode,
            ],
          });
          const captionAttr = element.getAttribute('data-caption');
          if (captionAttr) {
            try {
              const captionData = JSON.parse(captionAttr);
              if (captionData.editorState) {
                const editorState = caption.parseEditorState(captionData.editorState);
                if (!editorState.isEmpty()) {
                  caption.setEditorState(editorState);
                }
              }
            } catch (e) {
              console.warn('Failed to parse caption data:', e);
            }
          }

          if (src) {
            const node = $createImageNode({
              altText,
              height,
              maxWidth,
              showCaption,
              src,
              width,
              caption,
            });
            return { node };
          }
          return null;
        },
        priority: 1,
      }),
    };
  }

  static getType() {
    return 'image';
  }

  static clone(node) {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.__key,
    );
  }

  static importJSON(serializedNode) {
    const { altText, height, width, maxWidth, caption, src, showCaption } =
      serializedNode;
    const node = $createImageNode({
      altText,
      height,
      maxWidth,
      showCaption,
      src,
      width,
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  constructor(
    src,
    altText,
    maxWidth,
    width,
    height,
    showCaption = false,
    caption = createEditor({
      namespace: 'ImageNodeCaption',
      nodes: [
        RootNode, TextNode, LineBreakNode, ParagraphNode,
        LinkNode, AutoLinkNode, HashtagNode,
        EmojiNode, KeywordNode, MentionNode,
      ],
    }),
    captionsEnabled = true,
    key,
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__maxWidth = maxWidth;
    this.__width = width || 'inherit';
    this.__height = height || 'inherit';
    this.__showCaption = showCaption;
    this.__caption = caption;
    this.__captionsEnabled = captionsEnabled;
  }

  exportJSON() {
    return {
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height === 'inherit' ? 0 : this.__height,
      maxWidth: this.__maxWidth,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width === 'inherit' ? 0 : this.__width,
    };
  }

  exportDOM() {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    element.setAttribute('width', this.__width === 'inherit' ? 'auto' : this.__width.toString());
    element.setAttribute('height', this.__height === 'inherit' ? 'auto' : this.__height.toString());
    element.setAttribute('data-max-width', this.__maxWidth.toString());
    element.setAttribute('data-show-caption', this.__showCaption.toString());

    // Serialize caption as JSON attribute
    if (this.__caption) {
      try {
        const captionJSON = JSON.stringify(this.__caption.toJSON());
        element.setAttribute('data-caption', captionJSON);
      } catch (e) {
        console.warn('Failed to serialize caption:', e);
      }
    }

    return { element };
  }

  setWidthAndHeight(width, height) {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  setShowCaption(showCaption) {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  // View

  createDOM(config) {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM() {
    return false;
  }

  getSrc() {
    return this.__src;
  }

  getAltText() {
    return this.__altText;
  }

  decorate() {
    return (
      <ImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
        maxWidth={this.__maxWidth}
        nodeKey={this.getKey()}
        showCaption={this.__showCaption}
        caption={this.__caption}
        captionsEnabled={this.__captionsEnabled}
        resizable={true}
      />
    );
  }
}

export function $createImageNode({
  altText,
  height,
  maxWidth = 500,
  captionsEnabled,
  src,
  width,
  showCaption,
  caption,
  key,
}) {
  return $applyNodeReplacement(
    new ImageNode(
      src,
      altText,
      maxWidth,
      width,
      height,
      showCaption,
      caption,
      captionsEnabled,
      key,
    ),
  );
}

export function $isImageNode(node) {
  return node instanceof ImageNode;
}
